import queryString from 'query-string'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import ReactRouterPropTypes from 'react-router-prop-types'
import { Waypoint } from 'react-waypoint'

import { Button, Layout } from '@loadsmart/miranda-react'

import { Section } from 'core/ui/components/section/Section'
import { MainContent } from 'core/ui/MainContent'
import { plural } from 'core/utils/string'

import { LogoLoading } from 'legacy/components/LogoLoading/LogoLoading'
import { LOAD_BOARD_ACTIONS, LoadBoardContext } from 'legacy/contexts/loadBoard'
import { getOfferList } from 'legacy/services/offers'
import { sentry } from 'legacy/utils/sentry'
import user from 'legacy/utils/user'

import { useConfigContext } from 'config/ui/ConfigContext'
import { filterMapper } from 'loads/infra/offerMapper'
import { LoadboardFilters } from 'loads/ui/filter/LoadboardFilters'
import { expectedQueryParamNames as expectedRMISQueryParamNames } from 'loads/ui/rmis-onboarding/RMISOnboarding'
import { PendingResponses } from 'offers/ui/PendingResponses/PendingResponses'
import { SuggestPreferredLaneLoadboard } from 'preferred-lanes/ui/SuggestPreferredLaneLoadboard'
import { SpotQuoteList } from 'spot-quote/ui/SpotQuoteList'

import { expectedQueryParamNames as expectedBookDialogQueryParamNames } from '../BookDialog/routingConfig'
import { RMISBanner } from '../CarrierManager/RMIS/RMISBanner'
import { useAnalytics } from '../hooks/useAnalytics'
import { groupLoadOffersByDate } from '../utils/groupLoadOffersByDate'
import { sanitizeFiltersQuery } from '../utils/sanitizeFiltersQuery'
import { ThirdPartyRequestsList } from './Banners/ThirdPartyRequests/ThirdPartyRequestsList'
import { WrapperLoadMore } from './LoadBoard.styles'
import { NoOffersMessage } from './NoOffersMessage/NoOffersMessage'
import { OfferList } from './OfferList/OfferList'
import { TopOffers } from './TopOffers/TopOffers'

const UTM_PARAMS = ['utm_source', 'utm_campaign', 'utm_medium', 'ref_id']
const EXPECTED_PERSISTED_QUERY_PARAMS = [
  ...expectedBookDialogQueryParamNames,
  ...expectedRMISQueryParamNames,
  ...UTM_PARAMS,
  'bidding',
  'token',
]

const LOADS_PER_PAGE = 15

function LoadBoard(props) {
  const { history, location } = props

  /* context data */
  const [
    {
      isAwaitingScroll,
      loadOffersMap,
      persistedScrollPosition,
      persistedSearchQuery,
      totalOffersCount,
    },
    dispatch,
  ] = useContext(LoadBoardContext)

  /** Analytics */
  const { analytics } = useAnalytics()

  /* state */
  const [fetchError, setFetchError] = useState(false)
  const [filtersQuery, setFiltersQuery] = useState({})
  const [isFiltersReady, setFiltersReady] = useState(false)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [loadsCount, setLoadsCount] = useState({ invites: 0, response: 0 })

  const loadOffersList = useMemo(() => Object.values(loadOffersMap), [loadOffersMap])

  // Tracks if we reached the last available page on the backend
  const [reachedLastAvailablePageOnAPI, setReachedLastAvailablePageOnAPI] = useState(false)

  /* utils */
  const isUsingFilters = useMemo(() => Object.keys(filtersQuery).length > 0, [filtersQuery])
  const parsedQuery = useMemo(() => queryString.parse(location.search), [location.search])

  const fromDATRef = useRef((parsedQuery.utm_source || '').toLowerCase() === 'dat')

  const showOnboardingImprovements = useConfigContext().showOnboardingImprovements.toBoolean()
  const isBroker = user.get('entityType') === 'broker'

  const getApiQueryParams = useCallback(() => {
    const queryParams = filterMapper(filtersQuery)

    const params = {
      page_size: LOADS_PER_PAGE,
      page: page,
      ...queryParams,
    }

    return params
  }, [filtersQuery, page])

  /* data fetching */
  const getLoadOffers = useCallback(
    (page = 1) => {
      if (isBroker) return
      setLoading(true)
      setReachedLastAvailablePageOnAPI(false)
      setFetchError(false)

      if (page === 1 && isUsingFilters) {
        analytics.track(['Load Board', 'Search'], filtersQuery)
      }

      getOfferList({ params: getApiQueryParams() })
        .then(response => {
          let responseData = response.data || {}

          const listData = responseData.results || []
          const metadata = { count: responseData.count || 0 }

          if (!responseData.next) {
            setReachedLastAvailablePageOnAPI(true)
          }

          dispatch({
            type: LOAD_BOARD_ACTIONS.UPSERT_OFFERS,
            payload: {
              newLoadOffers: listData,
              totalOffersCount: metadata.count,
              fetchedSearchQuery: JSON.stringify(
                sanitizeFiltersQuery(filtersQuery, EXPECTED_PERSISTED_QUERY_PARAMS, true)
              ),
            },
          })

          if (page === 0 && !isUsingFilters) {
            analytics.track(['Load Board', 'Offers', 'Fetch'], {
              offersAvailable: metadata.count,
            })
          }
        })
        .catch(error => {
          const { response } = error || {}
          const { status, message } = response || {}

          if (!status) {
            sentry.log(error)
          }

          setFetchError(true)
          analytics.track(['Load Board', 'Offers', 'Fetch Error'], {
            description: message,
            status: status,
          })
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [isBroker, isUsingFilters, analytics, filtersQuery, dispatch, getApiQueryParams]
  )

  const getMoreLoadOffers = useCallback(() => {
    if (isBroker) return
    if (fetchError) return
    const newPage = page + 1
    setPage(newPage)
    analytics.track(['Load Board', 'Infinite Scroll'], {
      offset: (newPage - 1) * LOADS_PER_PAGE, // Send offset to support current analytics reports
    })
    getLoadOffers(newPage)
  }, [fetchError, page, analytics, getLoadOffers, isBroker])

  /* callbacks */
  const clearBoardAndFetchOffers = useCallback(() => {
    dispatch({ type: LOAD_BOARD_ACTIONS.CLEAR_FETCHED_OFFERS })
    setPage(1)
    getLoadOffers()
  }, [dispatch, getLoadOffers])

  const persistScrollPosition = useCallback(() => {
    dispatch({
      type: LOAD_BOARD_ACTIONS.PERSIST_SCROLL_POSITION,
      payload: {
        scrollPosition: window.scrollY,
        isAwaitingScroll: true,
      },
    })
  }, [dispatch])

  const restoreScrollPosition = useCallback(() => {
    window.scrollTo(0, persistedScrollPosition)
    dispatch({
      type: LOAD_BOARD_ACTIONS.PERSIST_SCROLL_POSITION,
      payload: {
        scrollPosition: window.scrollY,
        isAwaitingScroll: false,
      },
    })
  }, [dispatch, persistedScrollPosition])

  /* handlers */
  const handleCardRedirect = useCallback(
    event => {
      if (event.defaultPrevented) {
        return false
      }
      persistScrollPosition()
    },
    [persistScrollPosition]
  )

  const handleClearFilters = useCallback(() => {
    history.replace(`${location.pathname}`)
  }, [history, location.pathname])

  const handleFiltersReady = useCallback(ready => setFiltersReady(ready), [])

  const handleScrolledBottom = useCallback(() => getMoreLoadOffers(), [getMoreLoadOffers])

  const handlePendingChange = useCallback(
    count => setLoadsCount(current => ({ ...current, response: count })),
    []
  )
  const handleInvitesChange = useCallback(
    count => setLoadsCount(current => ({ ...current, invites: count })),
    []
  )

  /* effects */
  useEffect(() => {
    analytics.track(['Load Board', 'View'])
  }, [analytics])

  useEffect(() => {
    const strSanitizedFiltersQuery = JSON.stringify(
      sanitizeFiltersQuery(filtersQuery, EXPECTED_PERSISTED_QUERY_PARAMS, true)
    )

    if (isAwaitingScroll) {
      restoreScrollPosition()
    } else if (isFiltersReady && strSanitizedFiltersQuery !== persistedSearchQuery) {
      dispatch({
        type: LOAD_BOARD_ACTIONS.PERSIST_SEARCH_QUERY,
        payload: {
          searchQuery: strSanitizedFiltersQuery,
        },
      })
      clearBoardAndFetchOffers()
    }
  }, [
    clearBoardAndFetchOffers,
    dispatch,
    filtersQuery,
    isAwaitingScroll,
    isFiltersReady,
    persistedSearchQuery,
    restoreScrollPosition,
  ])

  useEffect(() => {
    const nextSanitized = sanitizeFiltersQuery(parsedQuery, EXPECTED_PERSISTED_QUERY_PARAMS)
    const nextSerialized = JSON.stringify(nextSanitized)
    const currentSerialized = JSON.stringify(filtersQuery)

    if (currentSerialized !== nextSerialized) {
      setFiltersQuery(nextSanitized)
    }
  }, [filtersQuery, parsedQuery])

  useEffect(() => {
    if (fromDATRef.current) {
      analytics.tag(['Load Board', 'DAT'])
    }
  }, [analytics])

  const loadsOffersListGroupedByDate = useMemo(() => {
    return groupLoadOffersByDate(loadOffersList).map(loadOfferGroup => (
      <OfferList
        date={loadOfferGroup.date}
        key={loadOfferGroup.date}
        loadOffers={loadOfferGroup.loadOffers}
        onCardRedirect={handleCardRedirect}
        separatorTopOffset={0}
      />
    ))
  }, [handleCardRedirect, loadOffersList])

  const shouldDisplayRMIS = !isBroker && !showOnboardingImprovements
  const shouldDisplayPendingResponses = !isUsingFilters
  const shouldDisplaySpotQuotes = !isUsingFilters

  const shouldDisplayTopOffers = !isUsingFilters
  const shouldDisplayOfferList = loadOffersList.length > 0
  const shouldFetchMore =
    shouldDisplayOfferList && !loading && !reachedLastAvailablePageOnAPI && !fetchError
  const shouldDisplayEmptyState =
    !loading && loadOffersList.length === 0 && loadsCount.invites === 0 && loadsCount.response === 0

  return (
    <MainContent>
      <Layout.Stack gap="spacing-8">
        <ThirdPartyRequestsList />

        {shouldDisplayRMIS && <RMISBanner />}

        {!isBroker && <SuggestPreferredLaneLoadboard />}

        {!isBroker && <LoadboardFilters onFilterReady={() => handleFiltersReady(true)} />}

        {shouldDisplayPendingResponses && <PendingResponses onChange={handlePendingChange} />}

        {shouldDisplaySpotQuotes && <SpotQuoteList onChange={handleInvitesChange} />}

        {!isBroker && (
          <>
            {shouldDisplayTopOffers && <TopOffers />}

            {shouldDisplayOfferList && (
              <Section
                title={`${totalOffersCount} ${plural('load', totalOffersCount)} available`}
                subtitle="Find the best offers for you bellow"
              >
                {loadsOffersListGroupedByDate}
              </Section>
            )}

            {loading && <LogoLoading />}

            {shouldFetchMore && (
              <WrapperLoadMore>
                <Waypoint onEnter={handleScrolledBottom} />
                <Button variant="tertiary" size="large" tabIndex="0" onClick={getMoreLoadOffers}>
                  Get more offers
                </Button>
              </WrapperLoadMore>
            )}
          </>
        )}
        {shouldDisplayEmptyState && (
          <NoOffersMessage
            onResetClick={handleClearFilters}
            filtersQuery={filtersQuery}
            entity={user.get('entityType')}
          />
        )}
      </Layout.Stack>
    </MainContent>
  )
}

LoadBoard.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
}

export { LoadBoard }
